import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Dropdown, Alert, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import ChatBubble from "../_components/common/chatBubble";
import LoadingScreen from "../_components/common/LoadingScreen";
import TravelMapModal from "../_modals/specific/TravelMapModal";
import FAQModal from "../_modals/specific/FAQModal";
import ItineraryUpdateModal from "../_modals/specific/ItineraryUpdateModal";
import { useCart } from '../context/CartContext';
import LateActivityModal from "../_modals/specific/LateActivityModal";
import { getGeolocation } from "../_utils/getGeolocation";
import { FAQS } from "../_utils/constant";
import { FaShoppingCart } from 'react-icons/fa'
import { ActivitiesContext } from "../context/ActivitiesContext";
import AllLocationsMapModal from "../_modals/specific/AllLocationsMapModal";

import "../styles/FirstDay.css";
import "../styles/global.css"
import "../styles/BudgetTiers.css";

// 缓存坐标数据
const coordinateCache = new Map();

// 预定义的fallback坐标，避免每次调用函数
const FALLBACK_COORDS = {
  "Le Serpent": [45.5144, -73.5546],
  "Old Montréal (Vieux-Montréal) and Old Port": [45.5020, -73.5540],
  "Joe Beef": [45.4843, -73.5801],
  "Notre-Dame Basilica": [45.5048, -73.5560],
  "Upstairs Jazz Bar & Grill": [45.4966, -73.5794],
  "Tommy Café": [45.5033, -73.5553],
  "Eggspectation": [45.5001, -73.5706],
  "Mount Royal Park": [45.5043, -73.5870],
  "Saint Joseph's Oratory of Mount Royal": [45.4910, -73.6188],
  "Pizzeria Bros (Old Montreal)": [45.5072, -73.5545],
  "La Banquise": [45.5231, -73.5780],
  "Montmorency Falls": [46.8900, -71.1505],
  "Old Quebec City": [46.8139, -71.2082],
  "Château Frontenac": [46.8110, -71.2065],
  "Hotel Checkout": [45.5017, -73.5673],
  "Travelodge Hotel by Wyndham Montreal Center": [45.5083, -73.5617],
  "Le Nouvel Hotel": [45.4924, -73.5760],
  "Fairmont the Queen Elizabeth Hotel": [45.5006, -73.5680],
};

// 酒店信息常量
const HOTEL_CONFIG = {
  Basic: {
    name: "Travelodge Hotel by Wyndham Montreal Center",
    coords: [45.50832593783939, -73.5617548642837],
  },
  Signature: {
    name: "Le Nouvel Hotel",
    coords: [45.4924495609113, -73.57605680684726],
  },
  Luxury: {
    name: "Fairmont the Queen Elizabeth Hotel",
    coords: [45.50056265986229, -73.56804069317572],
  },
};

// 字幕常量
const SUBTITLES = {
  "Day 1": "Day 1: Check in! Then Kick off your Montreal exploration!",
  "Day 2": "Day 2: Immerse yourself in history and art in the Old Port and Notre-Dame Basilica.",
  "Day 3": "Day 3: Savor poutine, take in Kondiaronk Belvedere's views, and visit the Museum of Fine Arts.",
  "Day 4": "Day 4: Marvel at Montmorency Falls, roam Old Quebec, and admire Château Frontenac.",
  "Day 5": "Day 5: Check out and head to the airport with fond memories."
};

// 静态数据
const TIERS = [
  {
    title: "Basic Tier",
    plan: "Basic",
    activities: [
      { time: "Check-in", description: "Arrive at Travelodge Hotel Montreal", price: "Free" },
      { time: "Sightseeing", description: "Explore Old Montreal", price: "$10" },
      { time: "Dinner", description: "Casual dining at Garde Manger", price: "$40" },
      { time: "Morning Hike", description: "Hike Mont Royal", price: "Free" },
      { time: "Cultural Visit", description: "Montreal Museum of Fine Arts", price: "$25" },
    ],
    totalPrice: "$999",
  },
  {
    title: "Signature Tier",
    plan: "Signature",
    activities: [
      { time: "Check-in", description: "Arrive at Le Nouvel Hotel Montreal" },
      { time: "Evening", description: "Wander through Old Montreal" },
      { time: "Dinner", description: "Enjoy dinner at Modavie" },
      { time: "Museum Visit", description: "Explore Museum of Fine Arts" },
      { time: "Afternoon Fun", description: "Visit La Ronde Amusement Park" },
    ],
    totalPrice: "$1699",
  },
  {
    title: "Luxury Tier",
    plan: "Luxury",
    activities: [
      { time: "Check-in", description: "Arrive at The Fairmont the Queen Elizabeth Hotel Montreal" },
      { time: "Luxury Dinner", description: "Dine at Maison Boulud" },
      { time: "Gourmet Dinner", description: "Dinner at Le Club Chasse" },
      { time: "Evening", description: "Relaxing on the St. Lawrence River" },
      { time: "Gourmet Dinner", description: "Dine at Toqué!" },
    ],
    totalPrice: "$3499",
  },
];

const BudgetTiers = ({ dayNumber, tier, dayActivities }) => {
  const { cartItems, addToCart } = useCart();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [showExtraText, setShowExtraText] = useState(false)
  const [searchInput, setSearchInput] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [modalData, setModalData] = useState({});
  const [showChatBubble, setShowChatBubble] = useState(false);
  const { planId, activities, setActivities, changeAllActivities } = useContext(ActivitiesContext);
  const [day1Unlocked, setDay1Unlocked] = useState(false);
  // ai传入的
  const [selectedStandardItems, setSelectedStandardItems] = useState([]); // 标准流程的选择
  const [showStandardCheckout, setShowStandardCheckout] = useState(false); // 是否显示checkout面板
  const [selectedStandardActivities, setSelectedStandardActivities] = useState([]);

  // 新增：从MyTrip传入的数据状态
  const [fromMyTrip, setFromMyTrip] = useState(false);
  const [customActivities, setCustomActivities] = useState([]);
  const [myTripBudgetTotal, setMyTripBudgetTotal] = useState(0);

  // 地图相关状态
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapDayIndex, setMapDayIndex] = useState(1);
  const [geoActivities, setGeoActivities] = useState([]);


  // 使用 useRef 来避免不必要的重渲染
  const abortControllerRef = useRef(null);

  // 优化的坐标获取函数
  const fallbackCoordsByName = useCallback((name) => {
    return FALLBACK_COORDS[name] || [45.50832593783939, -73.5617548642837];
  }, []);

  // 优化的坐标获取函数，使用缓存和防抖
  const fetchCoordinatesWithCache = useCallback(async (placeId) => {
    if (coordinateCache.has(placeId)) {
      return coordinateCache.get(placeId);
    }

    try {
      const response = await fetch(
        `https://citygetaway-backend-plan-service.vercel.app/api/place_lat_lng?place_id=${placeId}`,
        {
          headers: {
            "X-User-Name": "testDan",
            "X-User-Id": "681a6db71771cb427864f564",
          },
        }
      );

      if (!response.ok) {
        console.error(`Failed to fetch coords for place_id=${placeId}`);
        return null;
      }

      const data = await response.json();
      const lat = Number(data.lat);
      const lng = Number(data.lng);

      if (!data?.lat || !data?.lng || isNaN(lat) || isNaN(lng)) {
        console.warn(`Invalid coords for place_id=${placeId}`);
        return null;
      }

      const result = { lat, lng };
      coordinateCache.set(placeId, result);
      return result;
    } catch (error) {
      console.error(`Error fetching coords for place_id=${placeId}:`, error);
      return null;
    }
  }, []);

  // 优化的地图点击处理函数
  const handleDayMapClick = useCallback(async (dayItems, dayIndex) => {
    console.log(`📍 User clicked Map for Day ${dayIndex}`);

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const coordinatePromises = dayItems.map(async (activity) => {
      if (activity.place_id === "checkin_hotel" && Array.isArray(activity.location)) {
        const [lat, lng] = activity.location;
        if (typeof lat === "number" && typeof lng === "number") {
          return { name: activity.name, lat, lng };
        }
        return null;
      }

      if (!activity.place_id) {
        return null;
      }

      const coords = await fetchCoordinatesWithCache(activity.place_id);
      if (coords) {
        return { name: activity.name, lat: coords.lat, lng: coords.lng };
      }
      return null;
    });

    try {
      const allResults = await Promise.all(coordinatePromises);
      const validCoords = allResults.filter((pt) => pt !== null);

      if (validCoords.length === 0) {
        alert("No valid coordinates available for this day. Cannot open the map.");
        return;
      }

      setGeoActivities(validCoords);
      setMapDayIndex(dayIndex);
      setShowMapModal(true);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching coordinates:', error);
      }
    }
  }, [fetchCoordinatesWithCache]);

  // 优化的活动获取函数
  const fetchActivities = useCallback(async (place, dayIndex) => {
    const requestBody = {
      budget: 500,
      day_index: dayIndex,
      start_time: place.start_time,
      end_time: place.end_time,
      hotel_location: [45.4959, -73.6206],
      location: [45.4959, -73.6206],
      max_travel_time: 45,
      radius: 2500,
      plan_id: planId,
      place_id: place.place_id,
      preferences: ["Shopping", "Sightseeing"],
      replace_activities: [place.name],
      multi_day_itinerary: activities,
    };

    try {
      const response = await fetch(
        "https://citygetaway-backend-plan-service.vercel.app/api/change_activity",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Name": "testDan",
            "X-User-Id": "681a6db71771cb427864f564",
          },
          body: JSON.stringify(requestBody),
        }
      );
      return response.ok ? await response.json() : false;
    } catch (error) {
      console.error("Error fetching activities:", error.message || error);
      return false;
    }
  }, [planId, activities]);

  // 优化的活动更改处理函数
  const handleChangeActivity = useCallback(async (dayIndex, oldActivity) => {
    try {
      const res = await fetchActivities(oldActivity, dayIndex);
      if (!res || !res.multi_day_itinerary) throw new Error("Invalid response");

      const newDayPlan = res.multi_day_itinerary[dayIndex].plan;
      const newActivity = newDayPlan.find(act =>
        act.place_id !== oldActivity.place_id &&
        !activities[dayIndex].plan.some(existing =>
          existing.place_id === act.place_id
        )
      );

      if (!newActivity) {
        alert("⚠️ Could not find unique activity to replace.");
        return;
      }

      const updatedActivity = {
        ...newActivity,
        start_time: oldActivity.start_time,
        end_time: oldActivity.end_time,
      };

      setActivities(prev => {
        const updated = [...prev];
        updated[dayIndex].plan = updated[dayIndex].plan.map(act =>
          act.place_id === oldActivity.place_id ? updatedActivity : act
        );
        return updated;
      });
    } catch (err) {
      console.error("🔴 Failed to change activity:", err);
    }
  }, [fetchActivities, activities, setActivities]);

  // 优化的模态框处理函数
  const handleModalOpen = useCallback((type, data = {}) => {
    setModalType(type);
    setModalData(data);
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setModalType("");
    setModalData({});
  }, []);

  const handleHelpClick = useCallback(() => {
    handleModalOpen("faq", { faqs: FAQS });
  }, [handleModalOpen]);

  // 优化的搜索处理函数
  const handleSearchSubmit = useCallback((e) => {
    e?.preventDefault?.();

    const { name: hotelName, coords: hotelCoords } =
      HOTEL_CONFIG[selectedTier] || HOTEL_CONFIG.Basic;

    const checkinItem = {
      place_id: "checkin_hotel",
      name: hotelName,
      activity: "Hotel Check-in",
      location: hotelCoords,
      end_time: "08:00 AM",
      avg_cost_per_person: 0,
      image_url: "https://cf.bstatic.com/xdata/images/hotel/max1024x768/80101904.jpg?k=a278ce767982afbc083808368186e0f0a5cfc881bb0db51a3a26ede3165e26ca&o=&hp=1",
      activity_desc: `Check-in at ${hotelName} and get ready for your day!`,
      url: "",
    };

    setActivities(prev => prev.map(d => {
      if (d.day === "Day 1") {
        const originalPlan = d.plan || [];
        const filtered = originalPlan.filter(
          item => item.activity?.toLowerCase() !== "hotel check-in"
        );
        return {
          day: "Day 1",
          plan: [checkinItem, ...filtered],
        };
      }
      return d;
    }));

    setDay1Unlocked(true);
    setShowChatBubble(false);
  }, [selectedTier, setActivities]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  // 优化的层级选择函数
  const handleTierSelect = useCallback((tier) => {
    setLoading(true);
    setTimeout(() => {
      setSelectedTier(tier.plan);
      localStorage.setItem("selectedTier", tier.plan);
      setShowExtraText(true);
      setLoading(false);
    }, 1500);
  }, []);

  // 新增：处理从MyTrip来的数据
  const processMyTripData = useCallback(() => {
    if (!fromMyTrip || !customActivities.length) return;

    console.log('🔍 Processing MyTrip data:', customActivities.map(item => ({
      name: item.name,
      day_number: item.day_number
    })));
    const groupedByDay = customActivities.reduce((acc, activity) => {
      const dayKey = activity.day_number || activity.original_day || 1; // 使用传入的day_number
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(activity);
      return acc;
    }, {});

    console.log('🔍 Grouped by day details:', groupedByDay);
    console.log('🔍 Day keys:', Object.keys(groupedByDay));
    console.log('🔍 Each day count:', Object.entries(groupedByDay).map(([day, items]) => `Day ${day}: ${items.length} items`));
    // 转换为activities格式
    const convertedActivities = Object.entries(groupedByDay)
      .sort(([a], [b]) => parseInt(a) - parseInt(b)) // 按天数排序
      .map(([dayNum, dayActivities]) => ({
        day: `Day ${dayNum}`,
        plan: dayActivities.map(activity => ({
          place_id: activity.place_id,
          name: activity.name,
          location: activity.location,
          activity: activity.activity,
          start_time: activity.start_time,
          end_time: activity.end_time,
          avg_cost_per_person: activity.avg_cost_per_person,
          image_url: activity.image_url,
          activity_desc: activity.activity_desc,
          url: activity.url
        }))
      }));

    console.log('🔍 Converted activities:', convertedActivities);
    console.log('🔍 Converted activities days:', convertedActivities.map(day => day.day));
    setActivities(convertedActivities);
    setDay1Unlocked(true);
  }, [fromMyTrip, customActivities, setActivities]);

  // 新增：计算MyTrip预算统计
  const myTripBudgetStats = useMemo(() => {
    if (!fromMyTrip || !customActivities.length) return null;

    const totalCost = customActivities.reduce((sum, activity) => {
      return sum + (activity.avg_cost_per_person || 0);
    }, 0);

    const categoryCounts = customActivities.reduce((acc, activity) => {
      const category = activity.activity || 'Other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalItems: customActivities.length,
      totalCost,
      categoryCounts,
      averageCostPerDay: totalCost / Math.max(1, Object.keys(customActivities.reduce((acc, act) => {
        acc[act.planned_date || 'day1'] = true;
        return acc;
      }, {})).length)
    };
  }, [fromMyTrip, customActivities]);

  // 新增：添加自定义活动到购物车
  const handleAddCustomToCart = useCallback((activity) => {
    const cartItem = {
      place_id: activity.place_id,
      name: activity.name,
      category: activity.activity,
      cost: activity.avg_cost_per_person,
      image: activity.image_url,
      description: activity.activity_desc,
      location: activity.location,
      fromMyTrip: true
    };

    addToCart(cartItem);
  }, [addToCart]);

  // 新增：批量添加到购物车
  const handleAddAllToCart = useCallback(() => {
    customActivities.forEach(activity => {
      if (activity.activity === "Sightseeing" || activity.activity === "Activity") {
        handleAddCustomToCart(activity);
      }
    });

    navigate('/cart', {
      state: {
        selectedTier,
        fromMyTrip: true,
        myTripTotal: myTripBudgetTotal
      }
    });
  }, [customActivities, handleAddCustomToCart, navigate, selectedTier, myTripBudgetTotal]);

  // 优化的行程计算，使用更精确的依赖
  const computedItinerary = useMemo(() => {
    // 如果是从MyTrip来的，使用自定义活动
    // 如果是从MyTrip来的，使用activities数据（已经被processMyTripData处理过）
    if (fromMyTrip && activities.length > 0) {
      return activities.map((dayPlan, index) => ({
        title: `Day ${index + 1}`,
        subtitle: `Your custom itinerary for Day ${index + 1}`,
        items: (dayPlan.plan || []).map(act => ({
          name: act.name || act.location,
          place_id: act.place_id,
          start_time: act.start_time,
          end_time: act.end_time,
          cost: act.avg_cost_per_person,
          image: act.image_url,
          category: act.activity,
          bookingUrl: act.url,
          description: act.activity_desc,
          location: act.location || fallbackCoordsByName(act.name),
          fromMyTrip: true
        }))
      }));
    }

    // 原有的activities逻辑
    if (!activities.length) return [];

    const displayTier = selectedTier || "Basic";

    return activities.map((dayPlan, index) => {
      let planItems = dayPlan.plan;

      if (index === 0 && !day1Unlocked) {
        const hotel = HOTEL_CONFIG[displayTier];
        planItems = [
          {
            name: hotel.name,
            location: hotel.coords,
            activity: "Hotel Check-in",
            place_id: "checkin_hotel",
            start_time: "07:00 AM",
            end_time: "08:00 AM",
            avg_cost_per_person: 0,
            image_url: "https://cf.bstatic.com/xdata/images/hotel/max1024x768/80101904.jpg?k=a278ce767982afbc083808368186e0f0a5cfc881bb0db51a3a26ede3165e26ca&o=&hp=1",
            activity_desc: "Check-in at your hotel and get ready for your day!",
            url: "",
          }
        ];
      }

      if (index === activities.length - 1) {
        const hotel = HOTEL_CONFIG[displayTier];
        planItems = [
          {
            name: hotel.name,
            location: hotel.coords,
            activity: "Hotel Check-out",
            place_id: "checkin_hotel",
            start_time: "11:00 AM",
            end_time: "12:00 AM",
            avg_cost_per_person: 0,
            image_url: "https://cf.bstatic.com/xdata/images/hotel/max1024x768/80101904.jpg?k=a278ce767982afbc083808368186e0f0a5cfc881bb0db51a3a26ede3165e26ca&o=&hp=1",
            activity_desc: "Check-out at your hotel and get ready for your day!",
            url: "",
          }
        ];
      }

      return {
        title: `Day ${index + 1}`,
        subtitle: SUBTITLES[`Day ${index + 1}`],
        items: (planItems || []).map(act => ({
          name: act.name || act.location,
          place_id: act.place_id,
          start_time: act.start_time,
          end_time: act.end_time,
          cost: act.avg_cost_per_person,
          image: act.image_url,
          category: act.activity,
          bookingUrl: act.url,
          description: act.activity_desc,
          location: Array.isArray(act.location)
            ? act.location
            : act.location && act.location.lat && act.location.lng
              ? [act.location.lat, act.location.lng]
              : fallbackCoordsByName(act.location),
        }))
      };
    });
  }, [activities, selectedTier, day1Unlocked, fallbackCoordsByName, fromMyTrip, customActivities]);

  // Effect hooks 优化
  useEffect(() => {
    // 检查是否从MyTrip页面传入数据
    if (location.state?.fromMyTrip) {
      console.log('🔍 Received customActivities:', location.state.customActivities.map(item => ({
        name: item.name,
        day_number: item.day_number,
        original_day: item.original_day
      })));
      setFromMyTrip(true);
      setCustomActivities(location.state.customActivities || []);
      setMyTripBudgetTotal(location.state.totalEstimatedCost || 0);
      setSelectedTier(location.state.selectedTier || 'Basic');
      setShowExtraText(true);
      setDay1Unlocked(true);
    } else if (location.state?.selectedTier && !selectedTier) {
      setSelectedTier(location.state.selectedTier);
      setShowExtraText(true);
    }
  }, [location.state, selectedTier]);

  useEffect(() => {
    processMyTripData();
  }, [processMyTripData]);

  useEffect(() => {
    getGeolocation(setCurrentLocation);
    if (!day1Unlocked && !fromMyTrip) {
      const timer = setTimeout(() => setShowChatBubble(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [day1Unlocked, fromMyTrip]);
  // 处理标准流程的活动选择
  const handleStandardItemSelect = (itemId, activityData) => {
    setSelectedStandardItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });

    // 存储活动数据用于后续购买
    setSelectedStandardActivities(prev => {
      const filtered = prev.filter(item => item.id !== itemId);
      if (!prev.some(item => item.id === itemId)) {
        return [...filtered, { id: itemId, ...activityData }];
      }
      return filtered;
    });
  };

  // 全选/取消全选
  const handleSelectAllStandard = () => {
    const allSelectableItems = [];
    const allActivities = [];

    computedItinerary.forEach((day, di) => {
      day.items.forEach((place, idx) => {
        if (place.category === "Sightseeing" || place.category === "Entertainment") {
          const itemId = `${di}-${idx}-${place.place_id}`;
          allSelectableItems.push(itemId);
          allActivities.push({ id: itemId, ...place });
        }
      });
    });

    if (selectedStandardItems.length === allSelectableItems.length) {
      setSelectedStandardItems([]);
      setSelectedStandardActivities([]);
    } else {
      setSelectedStandardItems(allSelectableItems);
      setSelectedStandardActivities(allActivities);
    }
  };

  // 批量添加到购物车
  const handleAddAllStandardToCart = () => {
    selectedStandardActivities.forEach(activity => {
      const cartItem = {
        place_id: activity.place_id,
        name: activity.name,
        category: activity.category,
        cost: activity.cost,
        image: activity.image,
        description: activity.description,
        location: activity.location,
        fromStandardItinerary: true
      };
      addToCart(cartItem);
    });

    navigate('/cart', {
      state: {
        selectedTier,
        fromStandardItinerary: true,
        standardItineraryTotal: standardBudgetStats.totalCost
      }
    });
  };

  // 计算标准流程的预算统计
  const standardBudgetStats = useMemo(() => {
    const totalCost = selectedStandardActivities.reduce((sum, activity) => {
      return sum + (parseFloat(activity.cost || 0));
    }, 0);

    const categoryCounts = selectedStandardActivities.reduce((acc, activity) => {
      const category = activity.category || 'Other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalItems: selectedStandardActivities.length,
      totalCost,
      categoryCounts
    };
  }, [selectedStandardActivities]);

  // 计算可选择的活动总数
  const totalSelectableItems = useMemo(() => {
    return computedItinerary.reduce((count, day) =>
      count + day.items.filter(place =>
        place.category === "Sightseeing" || place.category === "Entertainment"
      ).length, 0
    );
  }, [computedItinerary]);
  // 清理函数
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 渲染优化的组件
  const renderTierCard = useCallback((tier, index) => (
    <Col md={4} key={index}>
      <Card className="mb-4 h-100">
        <Card.Body>
          <Card.Title className="text-center">
            <strong>{tier.title}</strong>
          </Card.Title>
          <ul className="budget-tiers-list">
            {tier.activities.map((activity, idx) => (
              <li key={idx}>
                <strong>{activity.time}:</strong> {activity.description}
              </li>
            ))}
            <li style={{ textAlign: "center" }}>
              <strong>Total Price:</strong> {tier.totalPrice}
            </li>
          </ul>
          <div className="text-center">
            <Button
              variant="primary"
              onClick={() => handleTierSelect(tier)}
            >
              Select
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Col>
  ), [handleTierSelect]);

  return (
    <>
      <ChatBubble
        showBubble={showChatBubble && !fromMyTrip}
        message="Need help? Click here for FAQs or updates!"
        onClick={handleHelpClick}
      />

      {/* 模态框 */}
      {modalType === "map" && <TravelMapModal show={showModal} onHide={handleModalClose} mapUrl={modalData.mapUrl} />}
      {modalType === "faq" && <FAQModal show={showModal} onHide={handleModalClose} faqs={modalData.faqs} />}
      {modalType === "itineraryUpdate" && (
        <ItineraryUpdateModal show={showModal} onHide={handleModalClose} message={modalData.message} />
      )}

      {loading ? (
        <LoadingScreen />
      ) : (
        <div className="budget-tiers">
          <Container style={{ padding: "40px 0" }}>
            {/* MyTrip来源的特殊头部 */}
            {fromMyTrip && (
              <div className="my-trip-header mb-4">
                <Alert variant="info" className="d-flex align-items-center">
                  <i className="bi bi-suitcase-lg me-3" style={{ fontSize: '1.5rem' }}></i>
                  <div className="flex-grow-1">
                    <h5 className="mb-1">Your Custom Trip Budget Plan</h5>
                    <p className="mb-0">
                      Created from your MyTrip selection • {customActivities.length} activities •
                      Estimated total: <strong>${myTripBudgetTotal.toFixed(2)}</strong>
                    </p>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate('/chat')}
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to MyTrip
                  </Button>
                </Alert>

                {/* MyTrip预算统计 */}
                {myTripBudgetStats && (
                  <Card className="mb-4">
                    <Card.Body>
                      <Row>
                        <Col md={8}>
                          <h6><i className="bi bi-graph-up me-2"></i>Budget Breakdown</h6>
                          <div className="d-flex gap-4 mb-3">
                            <div>
                              <strong>{myTripBudgetStats.totalItems}</strong> activities
                            </div>
                            <div>
                              Total cost: <strong>${myTripBudgetStats.totalCost.toFixed(2)}</strong>
                            </div>
                            <div>
                              Avg per day: <strong>${myTripBudgetStats.averageCostPerDay.toFixed(2)}</strong>
                            </div>
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            {Object.entries(myTripBudgetStats.categoryCounts).map(([category, count]) => (
                              <Badge key={category} bg="secondary">
                                {category}: {count}
                              </Badge>
                            ))}
                          </div>
                        </Col>

                        <Col md={4} className="text-end">
                          <Button
                            variant="success"
                            size="lg"
                            onClick={handleAddAllToCart}
                            className="w-100"
                          >
                            <FaShoppingCart className="me-2" />
                            Add All to Cart
                          </Button>
                          <small className="text-muted d-block mt-1">
                            Proceed to checkout
                          </small>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </div>
            )}

            {/* 原有的标题和介绍 */}
            {!fromMyTrip && (
              <>
                <h2 className="text-center mb-4">Discover Your Perfect Getaway: Choose Your Ideal Experience!</h2>
                {!showExtraText && (
                  <p className="intro-bubble position-relative">
                    Certainly! Here's a 5-day, 4-night sightseeing itinerary for Montreal,
                    tailored to three different budgets: basic, mid-range, and luxury.
                    Each plan includes estimated daily costs, accommodations, dining options,
                    and activities to help you make the most of your trip.
                  </p>
                )}

                {showExtraText && !fromMyTrip && (
                  <p className="intro-bubble position-relative">
                    Great choice! Based on your selected tier, here's your customized 5-day plan. Scroll down to see each day's activities laid out just for you.
                  </p>
                )}
              </>
            )}

            {!selectedTier && !fromMyTrip && (
              <Row className="budget-tiers-row">
                {TIERS.map(renderTierCard)}
              </Row>
            )}
          </Container>

          {(selectedTier || fromMyTrip) && computedItinerary.length > 0 && (
            <Container style={{ padding: "40px 0" }}>

              {!fromMyTrip && (selectedTier || fromMyTrip) && computedItinerary.length > 0 && (
                <Container style={{ padding: "20px 0" }}>
                  {/* 控制面板 */}
                  <div className="standard-checkout-controls mb-4">
                    <Card className="border-primary">
                      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">
                            <i className="bi bi-cart-plus me-2"></i>
                            Select Activities for Purchase
                          </h6>
                          <small>Choose activities you want to book and pay for</small>
                        </div>

                        <div className="d-flex gap-2">
                          <Button
                            variant="light"
                            size="sm"
                            onClick={handleSelectAllStandard}
                          >
                            {selectedStandardItems.length === totalSelectableItems ? 'Deselect All' : 'Select All'}
                          </Button>

                          <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => setShowStandardCheckout(!showStandardCheckout)}
                          >
                            {showStandardCheckout ? 'Hide' : 'Show'} Budget Preview
                          </Button>
                        </div>
                      </Card.Header>

                      {/* 预算预览面板 */}
                      {showStandardCheckout && selectedStandardItems.length > 0 && (
                        <Card.Body>
                          <Row>
                            <Col md={8}>
                              <h6><i className="bi bi-calculator me-2"></i>Budget Summary</h6>
                              <div className="d-flex gap-4 mb-2">
                                <div>
                                  <strong>{standardBudgetStats.totalItems}</strong> activities selected
                                </div>
                                <div>
                                  Total cost: <strong>${standardBudgetStats.totalCost.toFixed(2)}</strong>
                                </div>
                                <div>
                                  Avg per day: <strong>${(standardBudgetStats.totalCost / 5).toFixed(2)}</strong>
                                </div>
                              </div>

                              <div className="d-flex gap-2 flex-wrap">
                                {Object.entries(standardBudgetStats.categoryCounts).map(([category, count]) => (
                                  <Badge key={category} bg="secondary">
                                    {category}: {count}
                                  </Badge>
                                ))}
                              </div>
                            </Col>

                            <Col md={4} className="text-end">
                              <Button
                                variant="success"
                                size="lg"
                                onClick={handleAddAllStandardToCart}
                                disabled={selectedStandardItems.length === 0}
                                className="w-100"
                              >
                                <i className="bi bi-cart-plus me-2"></i>
                                Add All to Cart ({selectedStandardItems.length})
                              </Button>
                              <small className="text-muted d-block mt-1">
                                Proceed to checkout
                              </small>
                            </Col>
                          </Row>
                        </Card.Body>
                      )}
                    </Card>
                  </div>
                </Container>
              )}

              <Row>
                {console.log('🔍 Final computedItinerary:', computedItinerary.map(day => ({ title: day.title, itemCount: day.items.length }))) || null}
                {computedItinerary.map((day, di) => (
                  <Col md={6} lg={4} key={di} className="mb-4">
                    <Card className="h-100">
                      <Card.Body>
                        <Card.Title className="text-center mb-3">
                          {day.title}
                          <Button
                            className="map-button"
                            variant="light"
                            size="sm"
                            onClick={() => handleDayMapClick(day.items, di + 1)}
                            style={{
                              position: "absolute",
                              top: "12px",
                              right: "12px",
                              backgroundColor: "white",
                              color: "black",
                              borderRadius: "20px",
                              border: "2px solid #4CAF50",
                              padding: "0.25rem 0.6rem",
                              zIndex: 10,
                              display: "flex",
                              alignItems: "center",
                              gap: "6px"
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-map"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M15.817.113A.5.5 0 0 1 16 .5v14a.5.5 0 0 1-.402.49l-5 1a.5.5 0 0 1-.196 0L5.5 15.01l-4.902.98A.5.5 0 0 1 0 15.5v-14a.5.5 0 0 1 .402-.49l5-1a.5.5 0 0 1 .196 0L10.5.99l4.902-.98a.5.5 0 0 1 .415.103M10 1.91l-4-.8v12.98l4 .8zm1 12.98 4-.8V1.11l-4 .8zm-6-.8V1.11l-4 .8v12.98z"
                              />
                            </svg>
                            Map
                          </Button>
                        </Card.Title>

                        <Card.Subtitle
                          as="p"
                          className="mb-4 text-start text-muted fst-italic fs-6"
                          style={{ lineHeight: 1.6 }}
                        >
                          {day.subtitle || "brief overview"}
                        </Card.Subtitle>

                        {day.items.map((place, idx) => (
                          <div
                            className={`custom-card card mb-3 ${!fromMyTrip && selectedStandardItems.includes(`${di}-${idx}-${place.place_id}`)
                              ? 'border-primary shadow-sm'
                              : ''
                              }`}
                            key={place.place_id || `${di}-${idx}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0.75rem",
                              position: "relative",
                              transition: "all 0.2s ease"
                            }}
                          >
                            {/* 新增：选择框 */}
                            {!fromMyTrip && (
                              <Form.Check
                                type="checkbox"
                                className="position-absolute"
                                style={{ top: '10px', left: '10px', zIndex: 5 }}
                                checked={selectedStandardItems.includes(`${di}-${idx}-${place.place_id}`)}
                                onChange={() => handleStandardItemSelect(`${di}-${idx}-${place.place_id}`, place)}
                              />
                            )}
                            {/* MyTrip来源的项目显示特殊标识 */}
                            {place.fromMyTrip && (
                              <Badge
                                bg="info"
                                className="position-absolute"
                                style={{ top: '5px', left: '5px', fontSize: '0.7rem' }}
                              >
                                MyTrip
                              </Badge>
                            )}

                            <Dropdown
                              className="position-absolute"
                              style={{ top: 0, right: 0, zIndex: 10 }}
                              drop="down"
                            >
                              <Dropdown.Toggle
                                as="div"
                                style={{
                                  padding: '0.25rem',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                              >
                                ⋮
                              </Dropdown.Toggle>

                              <Dropdown.Menu align="end">
                                {!fromMyTrip && (
                                  <Dropdown.Item onClick={() => handleChangeActivity(di, place)}>
                                    Change Activity
                                  </Dropdown.Item>
                                )}
                                <Dropdown.Item
                                  onClick={() =>
                                    navigate(
                                      `/activity-description/${place.place_id}`,
                                      { state: { activity: place } }
                                    )
                                  }
                                >
                                  More Description
                                </Dropdown.Item>
                                {fromMyTrip && (
                                  <Dropdown.Item
                                    onClick={() => handleAddCustomToCart(place)}
                                    disabled={cartItems.some(item => item.place_id === place.place_id)}
                                  >
                                    <FaShoppingCart className="me-2" />
                                    {cartItems.some(item => item.place_id === place.place_id) ? 'In Cart' : 'Add to Cart'}
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>

                            <div className="card-body-itinerary-item" style={{ alignItems: "center", width: "100%" }}>
                              <div style={{ width: '4rem', textAlign: 'center', marginRight: '1.5rem' }}>
                                <img
                                  src={place.image}
                                  alt={place.name}
                                  style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                              </div>

                              <div className="card-details flex-grow-1" style={{ lineHeight: 1.4 }}>
                                <div><strong>{place.name}</strong></div>
                                <div>🏛️ {place.category || 'Activity'}</div>
                                <div>⏱️ Duration: {place.start_time} – {place.end_time}</div>
                                <div>💰 Estimated Cost (per person): {place.cost ? `${place.cost}` : 'N/A'}</div>
                              </div>

                              {place.category === "Sightseeing" && (() => {
                                const inCart = cartItems.some(item => item.place_id === place.place_id);
                                return inCart
                                  ? (
                                    <span className="badge in-cart-badge ms-auto"
                                      style={{ fontSize: '0.9rem', padding: '0.4em 0.6em' }}
                                    >
                                      In Cart
                                    </span>
                                  ) : (
                                    <Button
                                      className="cart-btn"
                                      variant="primary"
                                      size="sm"
                                      style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem' }}
                                      aria-label="add to cart"
                                      onClick={() => {
                                        if (fromMyTrip) {
                                          handleAddCustomToCart(place);
                                        } else {
                                          addToCart(place);
                                        }
                                        navigate('/cart', {
                                          state: {
                                            selectedTier,
                                            fromMyTrip,
                                            myTripTotal: myTripBudgetTotal
                                          }
                                        });
                                      }}
                                    >
                                      <FaShoppingCart size={16} />
                                    </Button>
                                  );
                              })()}
                            </div>
                          </div>
                        ))}

                        {day.title === "Day 1" && !day1Unlocked && !fromMyTrip && (
                          <Row>
                            <Col md={12}>
                              <Card className="mb-4">
                                <Card.Body>
                                  <Card.Title className="text-center">
                                    Invoke our Concord AI Travel Assistant
                                  </Card.Title>
                                  <Form>
                                    <Form.Group controlId="searchBox">
                                      <Form.Label>
                                        <i>
                                          If you let us know your exact arrival time in Montréal and your preferred check-in location, we can arrange everything accordingly for you.
                                        </i>
                                      </Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={6}
                                        defaultValue="I'll be checking in at the hotel at around 8:00 AM, Could you please suggest some activities for today?"
                                        onKeyDown={handleSearchKeyDown}
                                      />
                                    </Form.Group>
                                    <div className="text-center" style={{ paddingTop: "10px" }}>
                                      <Button variant="primary" onClick={handleSearchSubmit}>
                                        Submit
                                      </Button>
                                    </div>
                                  </Form>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <AllLocationsMapModal
                show={showMapModal}
                onHide={() => {
                  setShowMapModal(false);
                  setGeoActivities([]);
                }}
                activities={geoActivities}
                day={mapDayIndex}
              />
            </Container>
          )}
        </div>
      )}

      {modalType === "lateItinerary" && (
        <LateActivityModal
          show={showModal}
          onHide={() => setShowModal(false)}
          updatedItinerary={modalData}
          handleConfirm={() => {
            const updated = [...activities];
            updated[modalData.day_index].items = modalData.updated_items;
            setActivities(updated);
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default BudgetTiers;